<template>
  <div class="bill-sector flex justify-content-end p-2 bg-white">
    <div v-if="!hiddenPrice" class="flex flex-col w-full sm:w-1/2 sm:offset-1/2">
      <div v-if="tidyData.assort_discount" class="flex justify-between items-center">
        <span class="text-gray-600 font-medium">{{ $t("orders.assortDiscount") }}</span>
        <span class="text-red-500 font-bold">
          −
          {{ state?.currency_symbol }}
          {{ currencyFormat(tidyData.assort_discount) }}
        </span>
      </div>
      <div v-if="tidyData.prom_discount" class="flex justify-between items-center">
        <span class="text-gray-600 font-medium">{{ $t("orders.promDiscount") }}</span>
        <span class="text-red-500 font-bold">
          −
          {{ state?.currency_symbol }}
          {{ currencyFormat(tidyData.prom_discount) }}
        </span>
      </div>
      <div class="flex justify-between items-center">
        <span class="text-gray-600 font-medium">{{ $t("商品小計") }}</span>
        <span class="font-bold text-lg">
          {{ state?.currency_symbol }}
          {{ currencyFormat(tidyData.total_after_discount) }}
        </span>
      </div>
      <template v-if="!hideDeliveryFees">
        <div v-if="tidyData.remainDeliveryFees" class="flex justify-between items-start">
          <div class="flex-1 flex flex-col">
            <span class="text-gray-600 font-medium">
              {{ $t("運費") }}
            </span>
            <div v-if="tidyData.usedLogistics.length > 1" class="text-base text-red-500 mt-1">
              ‼️
              <a href="javascript:void(0)" class="font-normal text-red-700" @click="visibleShippingFeeDetails">
                {{ $t("您使用了多個物流方式，不適用免運條件") }}
              </a>
              ‼️
            </div>
            <div v-else-if="tidyData.amountBecomeFreeShipping" class="text-base text-gray-500 mt-1">
              ‼️
              <a href="javascript:void(0)" class="text-base font-normal text-gray-500" @click="visibleShippingFeeDetails">
                {{ $t("再買") }}
                <span class="font-bold text-red-700">
                  {{ `${state?.currency_symbol}${tidyData.amountBecomeFreeShipping}` }}
                </span>
                {{ $t("即可免運") }}
              </a>
              ‼️
            </div>
          </div>
          <span class="flex-none font-bold text-right">
            {{ state?.currency_symbol }}
            {{ currencyFormat(tidyData.remainDeliveryFees) }}
          </span>
        </div>
        <div v-else class="flex justify-between items-start">
          <div class="flex-1 flex flex-col">
            <span class="text-gray-600 font-medium">
              {{ $t("運費") }}
            </span>
            <span class="text-base font-normal text-gray-500">
              {{ $t("您已達到免運條件！") }}
            </span>
          </div>
          <div class="flex-none flex items-center text-right">
            <span class="bg-red-100 text-red-500 text-sm font-medium me-2 px-2.5 py-0.5 rounded">
              {{ $t("免運") }}
            </span>
            <span class="line-through text-gray-400 text-lg text-red-500">
              {{ `${state?.currency_symbol}${currencyFormat(deliveryFeesDiscount)}` }}
            </span>
          </div>
        </div>
      </template>
    </div>
    <el-dialog v-model="localState.shippingFeeDetails.visible" :title="`免運規則`" :width="isMobile ? '100%' : '600px'"
      :fullscreen="isMobile">
      <ShippingFeeDetails :form="form" :state="state" :data="data" :cart-groups-formatted="cartGroupsFormatted"
        :cart-event-groups-formatted="cartEventGroupsFormatted"
        @close="() => { localState.shippingFeeDetails.visible = false; }" />
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, reactive } from "vue";
import { useI18n } from "vue-i18n"
import { useVModel } from "@vueuse/core";
import { currencyFormat, breakpoint, lookupAliasTranslations } from "../../../utils";
import ShippingFeeDetails from "./ShippingFeeDetails.vue";

const props = defineProps({
  modelValue: Object,
  state: Object,
  data: Object,
  storeData: Object,
  cartGroupsFormatted: Object,
  cartEventGroupsFormatted: Object,
  deliveryFees: Number,
  deliveryFeesDiscount: Number,
  coinDiscountLimit: Number,
  hideDeliveryFees: Boolean,
});
const emit = defineEmits(["update:modelValue"]);

const form = useVModel(props, "modelValue", emit);

const { t, n, locale } = useI18n()

const isMobile = computed(() => breakpoint.value.xs || breakpoint.value.sm)
const hiddenPrice = computed(() => !!props.storeData?.settings?.hidden_price)
const hiddenCart = computed(() => !!props.storeData?.settings?.hidden_cart)
const hiddenContact = computed(() => !!props.storeData?.settings?.hidden_contact)
const aliasTranslations = computed(() => lookupAliasTranslations(props.storeData?.alias_translations, locale.value))

const tidyData = computed(() => {
  let assort_org_total = 0;
  let assort_discount = 0;
  let prom_discount = 0;
  let total_after_discount = 0;
  let amount_become_free_shipping = 0;
  Object.entries(props.cartGroupsFormatted).map(([k, v]) => {
    if (k !== "unchecked") {
      assort_org_total += v.assort_org_total;
      assort_discount += v.assort_discount;
      prom_discount += v.prom_discount;
      total_after_discount +=
        v.assort_org_total - v.assort_discount - v.prom_discount;
      amount_become_free_shipping = v.amount_become_free_shipping;
    }
  });
  
  // let isOuterIsland = false;
  // Object.values(props.state.isOuterIsland).map((val) => {
  //   if (val) {
  //     isOuterIsland = true;
  //   }
  // });
  return {
    assort_org_total,
    assort_discount,
    prom_discount,
    total_after_discount,
    usedLogistics: [
      ...new Set(
        form.value.carts
          .filter((elm) => elm.checked)
          .map((v) => v.delivery_type)
      ),
    ],
    remainDeliveryFees: props.deliveryFees - props.deliveryFeesDiscount,
    // isOuterIsland,
    amountBecomeFreeShipping: amount_become_free_shipping,
  };
});

const localState = reactive({
  shippingFeeDetails: {
    visible: false,
  },
});

const visibleShippingFeeDetails = () => {
  localState.shippingFeeDetails.visible = true;
};
</script>

<style lang="scss">
.bill-sector {
  content: '';
}
</style>